import React, { useCallback, memo } from 'react';
import { connect } from 'react-redux';
import { Grid, Paper } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import CreatePageConstants from '../CreatePageConstants';
import SettingsPanelSectionGeneral from './SettingsPanelSections/SettingsPanelSectionGeneral';
import SettingsPanelSectionVisualizer from './SettingsPanelSections/SettingsPanelSectionVisualizer';
import SettingsPanelSectionBackdrop from './SettingsPanelSections/SettingsPanelSectionBackdrop';
import SettingsPanelSectionText from './SettingsPanelSections/SettingsPanelSectionText';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/overlayscrollbars.css';
import SettingsPanelSectionAudio from './SettingsPanelSections/SettingsPanelSectionAudio';
import SettingsPanelSectionLyrics from './SettingsPanelSections/SettingsPanelSectionLyrics';
import { createActions } from '../store';
import debounce from 'lodash.debounce';
import SettingsPanelSectionElements from './SettingsPanelSections/SettingsPanelSectionElements';
import SettingsPanelSectionFeatures from './SettingsPanelSections/SettingsPanelSectionFeatures';
import SettingsPanelTabs from './SettingsPanelTabs';
import { executeCarefully } from '../../../../utilities/basicUtilities';

const styles = theme => ({
    container: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        height: '100%',
    },
    buttonsContainer: {
        maxHeight: `calc(100vh - calc(${theme.spacing(9)} * 2))`,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        flexWrap: 'nowrap',
    },
    panelContainer: {
        flexGrow: 1,
        maxWidth: 'calc(100% - 96px)'
    },
    panel: {
        height: '100%',
        maxHeight: `calc(100vh - calc(${theme.spacing(10)} * 2))`,
    },
    tabsScrollbar: {
        height: '100%'
    },
    contentScrollbar: {
        height: '100%'
    },
    contentScrollbarDisabled : {
        pointerEvents: 'none',
        opacity: 0.5,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        '-webkit-touch-callout': 'none',
    }
});

const SettingsPanel = ({ classes, currentSettingsPanel, setProperty, savingInProgress, saveVideoProject, navigate }) => {
    const save = useCallback((showSnack, onSaveComplete) => {
        if (savingInProgress) 
            return;

        saveVideoProject((videoId) => executeCarefully(onSaveComplete, videoId), showSnack, navigate);
    }, [savingInProgress, saveVideoProject, navigate]);

    const updatePanelScrollData = useCallback(debounce((instance) => {
        const { viewport } = instance.elements();
        const { scrollTop, offsetHeight } = viewport;

        setProperty('settingsPanelScrollData', {
            viewportStartPosition: scrollTop,
            viewportHeight: offsetHeight
        });
    }, 50), [setProperty]);
    
    const onPanelContentScroll = useCallback((instance, event) => {
        updatePanelScrollData(instance);
    }, [updatePanelScrollData]);

    const getSettingsPanelSection = useCallback((panel) => {
        switch(panel) {
            case CreatePageConstants.SETTINGS_PANELS.GENERAL: 
                return <SettingsPanelSectionGeneral save={save} />;
            case CreatePageConstants.SETTINGS_PANELS.AUDIO: 
                return <SettingsPanelSectionAudio save={save} />;
            case CreatePageConstants.SETTINGS_PANELS.VISUALIZER: 
                return <SettingsPanelSectionVisualizer />;
            case CreatePageConstants.SETTINGS_PANELS.BACKDROP: 
                return <SettingsPanelSectionBackdrop save={save} />;
            case CreatePageConstants.SETTINGS_PANELS.TEXT: 
                return <SettingsPanelSectionText />;
            case CreatePageConstants.SETTINGS_PANELS.LYRICS: 
                return <SettingsPanelSectionLyrics save={save} />;
            case CreatePageConstants.SETTINGS_PANELS.ELEMENTS:
                return <SettingsPanelSectionElements />;
            case CreatePageConstants.SETTINGS_PANELS.FEATURES:
                return <SettingsPanelSectionFeatures />;
            default:
                return null;
        }
    }, [save]);

    const contentScrollbarClass = savingInProgress ? `${classes.contentScrollbar} ${classes.contentScrollbarDisabled}` : classes.contentScrollbar;
    const contentAriaProps = { 'aria-disabled': savingInProgress || undefined };

    return (
        <Grid
            container
            direction='row'
            justifyContent='center'
            className={classes.container}
        >
            <Grid>
                <OverlayScrollbarsComponent
                    className={classes.tabsScrollbar}
                    options={{
                        overflow: {
                            x: 'hidden'
                        }
                    }}
                >
                    <SettingsPanelTabs />
                </OverlayScrollbarsComponent>
            </Grid>
            <Grid className={classes.panelContainer}>
                <Paper
                    className={classes.panel}
                >
                    <OverlayScrollbarsComponent
                        className={contentScrollbarClass}
                        {...contentAriaProps}
                        events={{ scroll: onPanelContentScroll }}
                    >
                        {getSettingsPanelSection(currentSettingsPanel)}
                    </OverlayScrollbarsComponent>
                </Paper>
            </Grid>
        </Grid>
    );
}

const mapStateToProps = (state) => {
    const { currentSettingsPanel } = state.create;
    const { savingInProgress } = state.create.videoSavingState;
    return { currentSettingsPanel, savingInProgress };
};

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const saveVideoProject = (onSaveComplete, showSnack, navigate) => dispatch(createActions.saveVideoProject(onSaveComplete, showSnack, navigate));
    return { setProperty, saveVideoProject };
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(withStyles(SettingsPanel, styles)));